import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { useEditor } from '@toasttab/sites-components'
import classnames from 'classnames'
import {
  ImageRatio,
  MenuConfig,
  MenuFormatConfig,
  MenuTemplate,
  PlaceHolderImageGenerationEnum,
  Theme
} from 'src/apollo/sites'
import { CatalogProductType } from 'src/apollo/onlineOrdering'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { formatImageURL, slugify } from 'src/lib/js/utilities'
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice'
import PriceFromRange from 'src/shared/components/common/price/PriceFromRange'
import {
  getMinimumPrice,
  hasSizeBasedPricing
} from 'src/shared/utils/priceUtils'
import Image from 'shared/components/common/Image'
import Link from 'shared/components/common/link'
import { useModal } from 'shared/components/common/modal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import {
  getMenuPath,
  useRestaurantRoutes
} from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'

import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme'
import { useImageAspectRatio } from 'src/shared/js/hooks/useImageAspectRatio'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'
import ItemModal from 'public/components/default_template/online_ordering/item_modal/ItemModal'
import {
  MenuItemTags,
  OfferBadge,
  OutOfStockTag
} from 'public/components/default_template/online_ordering/item_tags/MenuItemTags'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useOffers } from 'public/components/online_ordering/OffersContext'
import { useOptionalTimeBasedRules } from 'public/components/online_ordering/TimeBasedRuleContext'
import { Channel } from 'public/js/siteUtilities'

import { DefaultPlaceHolderMenuImage } from './DefaultPlaceHolderMenuImage'
import { DoProduct, MenuItem } from './MenuSection'
import { TemplateOverrides } from './menuStylingOverrides'
import {
  CartDeliveryInfo,
  useFulfillment
} from 'src/public/components/online_ordering/FulfillmentContext'
import { ItemSource } from '../online_ordering/cart/cartUtils'
import urljoin from 'url-join'
import { usePageDomains } from 'public/js/PageDomainContext'
import QuickAddToCart from './QuickAddToCart'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

export const CLICKED_MENU_ITEM_EVENT_NO_NAME_STR = 'No name'

export const IMAGE_SUFFIX_REGEX = /\.(jpg|png)$/

/**
 * Determines if out-of-stock styles should be applied based on the channel. Out-of-stock styles are disabled for ecomm.
 */
export const shouldApplyOutOfStockStyles = (
  item: MenuItem,
  channel?: Channel
): boolean => {
  return channel !== Channel.ECOMM && !!item.outOfStock
}

export const getMenuOverrides = (
  theme: Theme,
  channel?: Channel,
  isReadOnly?: boolean
) => {
  if (channel === Channel.ECOMM) {
    return theme.colorOverrides?.ecommMenu
  }
  if (isReadOnly) {
    return theme.colorOverrides?.viewOnlyMenu
  }
  return theme.colorOverrides?.onlineOrderingMenu
}

export type ToastImageSet = {
  xs?: string
  small?: string
  medium?: string
  large?: string
}

type MenuItemImageSet = {
  '140w'?: string
  '300w'?: string
  '600w'?: string
  '900w'?: string
}

export const getMenuItemSrcSetInProd = (
  images?: ToastImageSet | null
): MenuItemImageSet => {
  // TODO: When preproduction images are fixed, remove the NODE_ENV check.
  if (
    (process.env.NODE_ENV === 'production' ||
      process.env.NODE_ENV === 'prod') &&
    images
  ) {
    return getMenuItemSrcSet(images)
  }
  return {}
}

// These source set URLs are served by the toast photo service
// and are guaranteed to exist
export const getMenuItemSrcSet = (images: ToastImageSet): MenuItemImageSet => {
  const imgSet: MenuItemImageSet = {}
  if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'prod')
    return imgSet
  if (images.xs) {
    imgSet['140w'] = formatImageURL(images.xs)
  }
  if (images.small) {
    imgSet['300w'] = formatImageURL(images.small)
  }
  if (images.medium) {
    imgSet['600w'] = formatImageURL(images.medium)
  }
  if (images.large) {
    imgSet['900w'] = formatImageURL(images.large)
  }
  return imgSet
}

export const getMenuItemSizes = (
  formatConfig?: MenuFormatConfig | null
): string => {
  // The side image container is 136px tall with object-fit: cover. Landscape
  // images (e.g. 5:3) need width ≥ ~227px to fill the height without upscaling.
  // Using 200px ensures the browser selects the 300w srcSet image instead of
  // the 140w (xs) image, which is only 176×106 and would be upscaled/blurry.
  const defaultSideImageSize = '200px'
  if (!formatConfig) {
    return defaultSideImageSize
  }
  if (formatConfig.template === MenuTemplate.TopImageV2) {
    let sizes = '(min-width: 1024px) 33vw, (min-width: 560px) 50vw, '
    // The mobile optimization setting is used to determine if the image
    // should be full width on mobile, or if it should be a side image.
    if (formatConfig.mobileOptimization) {
      sizes += '116px'
    } else {
      sizes += '100vw'
    }
    return sizes
  }
  if (formatConfig?.imageRatio === ImageRatio.Larger) {
    // This is the slightly wider image ratio.
    return '182px'
  }
  return defaultSideImageSize
}

export const getTemplateClass = (
  template: MenuTemplate | undefined | null
): string => {
  if (!template) return ''
  const classStr = template ? template.toString() : ''
  if (!classStr) return ''
  return classStr.charAt(0).toLowerCase() + classStr.slice(1)
}

type Props = {
  item: MenuItem
  product?: DoProduct
  menuGuid?: string | null
  id?: string
  setIsMapOrModalOpen?: (isOpen: boolean) => void
  // True if the location map is open, or if any card has the modal open.
  isMapOrModalOpen?: boolean
  // True for ephemeral menu items that shouldn't be linked to.
  preventDeeplink?: boolean
  isReadOnly?: boolean
  menuConfig?: MenuConfig | null
  templateOverrides?: TemplateOverrides
  channelGuid?: string
  itemSource?: ItemSource
}

type ItemContentProps = {
  item: MenuItem
  product?: DoProduct
  menuGuid?: string | null
  menuConfig?: MenuConfig | null
  templateOverrides?: TemplateOverrides
  isReadOnly?: boolean
  channelGuid?: string
  itemSource?: ItemSource
}

type Params = {
  itemSlug?: string
  itemGuid?: string
  guid?: string
}

// React Router doesn't provide functionality for changing the current route
// without rerendering, so we use native browser functionality
const replaceUrl = (newUrl: string) => {
  window.history.replaceState(null, '', newUrl)
}

export const ItemContent = (props: ItemContentProps) => {
  const { item, product, menuConfig, templateOverrides } = props
  const {
    restaurant: {
      config,
      meta: { backgroundColor: pageBackgroundColor }
    }
  } = useRestaurant()
  const variableMenuSpacing = templateOverrides?.variableMenuSpacing
  const { channel } = useCart()
  const getColorFromTheme = useThemeColorScheme()
  const { ooImageAspectRatio, ooImageAspectRatioThreshold } = useFlags()
  const { timeBasedRulesMap } = useOptionalTimeBasedRules() || {
    timeBasedRulesMap: {} as NonNullable<
      ReturnType<typeof useOptionalTimeBasedRules>
    >['timeBasedRulesMap']
  }
  const { itemOffers, getOffersTextForEntity } = useOffers()
  const numItemOffers = item.guid ? itemOffers[item.guid]?.length ?? 0 : 0
  const totalOffers = getOffersTextForEntity(
    item.guid,
    item.itemGroupGuid,
    props.menuGuid
  ).length
  const mobileOptimized =
    useIsMobile(ScreenWidth.EXTRA_SMALL) &&
    templateOverrides?.enableMobileOptimization
  const backgroundColor = useMemo(() => {
    return getColorFromTheme(
      (theme) => {
        const surfaceDefault = theme.colorScheme.surface.default
        const menuOverrides = getMenuOverrides(theme, channel, props.isReadOnly)
        return mobileOptimized
          ? surfaceDefault
          : menuOverrides?.cardBackground ?? surfaceDefault
      },
      mobileOptimized ? pageBackgroundColor : menuConfig?.colors?.background
    )
  }, [
    getColorFromTheme,
    menuConfig?.colors?.background,
    mobileOptimized,
    pageBackgroundColor,
    props.isReadOnly,
    channel
  ])

  const timeBasedRules = item.guid && timeBasedRulesMap[item.guid]

  // Check if there are any item tags that will be displayed
  const hasAnyItemTags = !!(
    timeBasedRules ||
    numItemOffers > 0 ||
    item.itemTags?.length ||
    item.outOfStock
  )

  const imgSrc = useMemo(
    () =>
      item.imageUrls?.medium ||
      item.imageUrls?.sm ||
      item.imageUrls?.large ||
      item.imageUrls?.xs,
    [item.imageUrls]
  )

  const menuTemplateConfig = templateOverrides?.menuTemplateOverride
  const itemDescriptionTextColor = menuConfig?.colors?.secondaryText
  const contentStyle = useMemo(() => {
    const legacyStyle = itemDescriptionTextColor
      ? { color: itemDescriptionTextColor }
      : {}

    return getColorFromTheme((theme) => {
      const menuOverrides = getMenuOverrides(theme, channel, props.isReadOnly)
      return {
        color:
          menuOverrides?.itemDescription ?? theme.colorScheme.text.secondary
      }
    }, legacyStyle)
  }, [getColorFromTheme, itemDescriptionTextColor, props.isReadOnly, channel])

  const ooBasic =
    config.isOnlineOrderingOnly && config.hasFullCustomization === false
  const sideImage =
    !menuTemplateConfig || menuTemplateConfig === MenuTemplate.SideImage
  const squareImage = sideImage && (ooBasic || menuConfig?.format?.squareImages)
  const imageRatioClass =
    templateOverrides?.imageRatio || (squareImage ? 'squareImage' : '')
  const hideDescription = templateOverrides?.hideMenuItemDescription || false

  // Detect actual image aspect ratio to intelligently apply object-fit
  // Only apply when feature flag is enabled
  // Parse threshold from feature flag, default to 0.7 if not provided or invalid
  const threshold = parseFloat(ooImageAspectRatioThreshold || '0.7') || 0.7
  const { aspectRatioClass: detectedAspectRatioClass, onImageLoad } =
    useImageAspectRatio(threshold)
  const imageAspectRatioClass = ooImageAspectRatio
    ? detectedAspectRatioClass
    : undefined

  const itemImage = useMemo(() => {
    const className = classnames(
      'itemImage',
      imageRatioClass,
      imageAspectRatioClass,
      {
        hideImage: templateOverrides?.hideImages,
        outOfStock: shouldApplyOutOfStockStyles(item, channel)
      }
    )
    if (item?.image) {
      return (
        <Image
          alt=''
          src={item.image}
          className={className}
          data-testid={item.name + '-image'}
          onLoad={ooImageAspectRatio ? onImageLoad : undefined}
        />
      )
    } else if (imgSrc && item.imageUrls) {
      return (
        <Image
          alt=''
          src={formatImageURL(imgSrc)}
          srcSet={getMenuItemSrcSet(item.imageUrls)}
          sizes={getMenuItemSizes(menuConfig?.format)}
          className={className}
          data-testid={item.name + 'image'}
          onLoad={ooImageAspectRatio ? onImageLoad : undefined}
        />
      )
    } else if (
      templateOverrides?.placeholderImageConfig.generatePlaceholderImage ===
      PlaceHolderImageGenerationEnum.RepeatedText
    ) {
      return (
        <div className={classnames(className, 'placeholderImg')}>
          <DefaultPlaceHolderMenuImage templateOverrides={templateOverrides} />
        </div>
      )
    } else {
      return null
    }
  }, [
    imageRatioClass,
    imageAspectRatioClass,
    templateOverrides,
    item,
    channel,
    imgSrc,
    menuConfig?.format,
    onImageLoad,
    ooImageAspectRatio
  ])

  // The following code is used to determine if the item tags should be hidden because they are pushing the itemInfo out of the container
  const itemInfoRef = useRef<HTMLDivElement>(null)
  const [hideMenuItemTags, setHideMenuItemTags] = useState(false)
  const [hasWindowResized, setHasWindowResized] = useState(false)

  useEffect(() => {
    // The timeout allows itemTags to calculate how many tags to show before we check the sizing
    setTimeout(() => {
      if (!hideMenuItemTags || hasWindowResized) {
        setHasWindowResized(false)
        const itemInfoEl = itemInfoRef.current
        setHideMenuItemTags(
          !!itemInfoEl?.offsetLeft && itemInfoEl.offsetLeft - 16 < 0
        )
      }
    }, 200)
  }, [hasWindowResized, hideMenuItemTags])

  // We use the timeoutRef to debounce the window resize event
  const timeOutRef = useRef<any>(false)
  // Everytime the window resizes, we want to show the item tags to see if they fit
  const showItemTags = useCallback(() => {
    if (timeOutRef.current !== false) {
      clearTimeout(timeOutRef.current)
    }
    timeOutRef.current = setTimeout(() => {
      setHideMenuItemTags(false)
      setHasWindowResized(true)
    }, 100)
  }, [])
  useEffect(() => {
    if (window) {
      window.addEventListener('resize', showItemTags)
    }
  }, [showItemTags])

  if (
    menuTemplateConfig === MenuTemplate.Condensed ||
    menuTemplateConfig === MenuTemplate.ExtraCondensed ||
    menuTemplateConfig === MenuTemplate.Stacked
  ) {
    return (
      <div
        className={classnames('itemInfo', getTemplateClass(menuTemplateConfig))}
        ref={itemInfoRef}
      >
        <div className='itemHeadAvailDesc'>
          <div className='itemHeader'>
            <h3
              style={{
                fontSize: 'inherit',
                fontFamily: 'var(--tsw-header-font-family, Effra, sans-serif)'
              }}
              aria-hidden={true}
            >
              <span className='headerText'>{item.name ?? ''}</span>
            </h3>
          </div>
          {item.description && !hideDescription && (
            <div style={contentStyle} className='itemDescription'>
              {item.description}
            </div>
          )}
          <div className='priceAvailability'>
            {!hideMenuItemTags && (
              <MenuItemTags item={item} backgroundColor={backgroundColor} />
            )}
            <PriceLine item={item} product={product} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {numItemOffers > 0 && !item.outOfStock && !mobileOptimized ? (
        <OfferBadge
          text={totalOffers > 1 ? 'OFFERS' : 'OFFER'}
          className={classnames(
            'offerBadge',
            menuTemplateConfig === MenuTemplate.LeftImageV2
              ? 'leftAlign'
              : 'rightAlign'
          )}
        />
      ) : null}
      {itemImage}
      <div
        className={classnames('itemInfo', {
          outOfStock: shouldApplyOutOfStockStyles(item, channel)
        })}
        ref={itemInfoRef}
      >
        <div
          className={classnames('itemHeader', {
            hasTag: timeBasedRules,
            hasItemTags: hasAnyItemTags,
            variableHeight: variableMenuSpacing
          })}
        >
          <h3
            style={{
              fontSize: 'inherit',
              fontFamily: 'var(--tsw-header-font-family, Effra, sans-serif)'
            }}
            aria-hidden={true}
          >
            <span className='headerText'>{item.name ?? ''}</span>
          </h3>
          {/* Description added to the header so we can clamp ellipsis to 3 lines as a group */}
          {item.description && !hideDescription && (
            <div
              data-testid='item-content-description'
              style={contentStyle}
              className={classnames('itemDescription', {
                variableHeight: variableMenuSpacing
              })}
            >
              {item.description ?? ''}
            </div>
          )}
        </div>
        <div className='priceAvailability'>
          {!hideMenuItemTags && !variableMenuSpacing && (
            <MenuItemTags
              item={item}
              backgroundColor={backgroundColor}
              numOffers={mobileOptimized && numItemOffers > 0 ? totalOffers : 0}
            />
          )}
          <div className='priceLine'>
            <PriceLine item={item} product={product} />
            {variableMenuSpacing && item.outOfStock && <OutOfStockTag />}
          </div>
        </div>
      </div>
    </>
  )
}

export const PriceLine = ({
  item,
  product
}: {
  item: MenuItem
  product?: DoProduct
}) => {
  const { channel } = useCart()
  const perUnitString =
    item.usesFractionalQuantity && item.unitOfMeasure
      ? `/${item.unitOfMeasure.toLowerCase()}`
      : ''
  if (!item.price && !item.prices?.length) {
    return null
  }
  const value = getMinimumPrice(item.price, item.prices)
  const defaultModifiersPrices = item.defaultModifiersPrice || 0
  const unit = hasSizeBasedPricing(item.prices) ? '+' : ''
  return (
    <span
      className={classnames('price', {
        outOfStock: shouldApplyOutOfStockStyles(item, channel)
      })}
      data-testid={`price-${item.guid}`}
    >
      {!product ? (
        <>
          <FormattedPrice value={value + defaultModifiersPrices} />
          {unit}
        </>
      ) : (
        <PriceFromRange price={item.price} prices={product.prices} />
      )}
      {perUnitString}
    </span>
  )
}

const MenuItemCard = (props: Props) => {
  const { isEditor } = useEditor()

  return isEditor ? (
    <MenuItemCardBase
      {...props}
      preventDeeplink={true}
      disableClickEvents={true}
    />
  ) : (
    <MenuItemCardWithRouterParamsAndTracker {...props} />
  )
}

const MenuItemCardWithRouterParamsAndTracker = (props: Props) => {
  const params = useParams<Params>()
  const { track } = useThrottledTracker()
  const { channel } = useCart()
  const location = useLocation()
  const { host, pageDomainOverrides } = usePageDomains()
  const showItemPDP =
    channel === Channel.ECOMM ||
    location.pathname.startsWith('/menu') ||
    pageDomainOverrides.get('/menu') === host
  return (
    <MenuItemCardBase
      {...props}
      track={track}
      params={params}
      showItemPDP={showItemPDP}
    />
  )
}

type BaseProps = Props & {
  track?: (name: any, data: any) => void
  params?: Params
  disableClickEvents?: boolean
  showItemPDP?: boolean
}

const menuItemStyle = (
  item: MenuItem,
  menuConfig?: MenuConfig | null,
  theme?: Theme | undefined | null,
  isReadOnly: boolean = false,
  channel?: Channel
) => {
  const applyOutOfStockStyle = shouldApplyOutOfStockStyles(item, channel)
  if (theme?.enabled) {
    const menuColorOverrides = getMenuOverrides(theme, channel, isReadOnly)
    return {
      ...(menuColorOverrides?.cardBackground && {
        backgroundColor: menuColorOverrides?.cardBackground
      }),
      ...(menuColorOverrides?.itemTitle &&
        !applyOutOfStockStyle && { color: menuColorOverrides?.itemTitle })
    }
  } else {
    return {
      ...(menuConfig?.colors?.background && {
        backgroundColor: menuConfig.colors.background
      }),
      ...(menuConfig?.colors?.primaryText &&
        !applyOutOfStockStyle && { color: menuConfig.colors.primaryText })
    }
  }
}

const MenuItemCardBase = ({
  disableClickEvents = false,
  showItemPDP,
  itemSource,
  templateOverrides,
  ...props
}: BaseProps) => {
  const track = props.track
  const ref = useRef<HTMLLIElement>(null)
  const { item, product, setIsMapOrModalOpen, isReadOnly } = props
  const { channel, cartGuid } = useCart()
  const { fulfillmentData } = useFulfillment()
  const { orderingDisabled } = useRestaurant()
  const {
    selectedLocation,
    restaurant: { theme }
  } = useRestaurant()
  const { orderPath, ecommPath } = useRestaurantRoutes()
  const { isOpen, onOpen, onClose } = useModal()
  const getColorFromTheme = useThemeColorScheme()
  const path = channel === Channel.ECOMM && ecommPath ? ecommPath : orderPath
  const variableMenuSpacing = templateOverrides?.variableMenuSpacing

  // Quick add states
  const [showQuickAddSuccessIcon, setShowQuickAddSuccessIcon] = useState(false)

  const productDisplayPageHref = useMemo(() => {
    return channel === Channel.ECOMM
      ? ecommMenuItemPath(ecommPath || '', item, product)
      : menuItemPagePath(
          getMenuPath(selectedLocation.shortUrl),
          item.itemGroupGuid,
          item.name || '',
          item.guid
        )
  }, [channel, ecommPath, item, product, selectedLocation.shortUrl])
  const handleFocus = () => {
    if (ref.current) {
      const { top } = ref.current.getBoundingClientRect()
      // If the top of the element is less than 105px from the top of the window,
      // it's most likely obscured by the menu nav.  Scroll it into view.
      if (top <= 105) {
        // Use an offset of 260px to account for the global nav, menu nav, and potentially
        // the spotlight banner, which are present on scroll-up but not scroll-down
        window.scrollTo({ top: top + window.scrollY - 260 })
      }
    }
  }

  const openModal = useCallback(() => {
    onOpen()
    if (setIsMapOrModalOpen) {
      setIsMapOrModalOpen(true)
    }
  }, [onOpen, setIsMapOrModalOpen])

  const closeModal = useCallback(() => {
    onClose()
    if (setIsMapOrModalOpen) {
      setIsMapOrModalOpen(false)
    }
    if (!isReadOnly) {
      replaceUrl(path)
    }
  }, [onClose, setIsMapOrModalOpen, isReadOnly, path])

  const onItemClick = useCallback(
    (e: React.MouseEvent) => {
      onItemClickImpl(
        e,
        isReadOnly,
        item,
        track,
        path,
        openModal,
        cartGuid,
        fulfillmentData?.cartFulfillmentData?.deliveryInfo,
        itemSource
      )
    },
    [
      item,
      isReadOnly,
      track,
      path,
      openModal,
      cartGuid,
      fulfillmentData?.cartFulfillmentData?.deliveryInfo,
      itemSource
    ]
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      onItemKeydownImpl(
        e,
        isReadOnly,
        item,
        track,
        path,
        openModal,
        cartGuid,
        fulfillmentData?.cartFulfillmentData?.deliveryInfo,
        itemSource
      )
    },
    [
      isReadOnly,
      item,
      track,
      path,
      openModal,
      cartGuid,
      fulfillmentData?.cartFulfillmentData?.deliveryInfo,
      itemSource
    ]
  )

  const onItemSuccessfullyAdded = useCallback(() => {
    setShowQuickAddSuccessIcon(true)
    setTimeout(() => {
      setShowQuickAddSuccessIcon(false)
    }, 2500)
  }, [setShowQuickAddSuccessIcon])

  if (!item.name) {
    return null
  }

  const itemStyle = menuItemStyle(
    item,
    props.menuConfig,
    theme,
    isReadOnly,
    channel
  )

  const itemReference = !isReadOnly
    ? channel === Channel.ECOMM
      ? ecommMenuItemPath(ecommPath || '', item, product)
      : menuItemPath(orderPath, item.name, item.guid)
    : undefined
  const menuTemplateConfig = templateOverrides?.menuTemplateOverride
  const borderColor = getColorFromTheme((theme) => {
    const menuOverrides = getMenuOverrides(theme, channel, isReadOnly)
    return menuOverrides?.cardBorder ?? theme.colorScheme.border.default
  }, templateOverrides?.borderColor)

  const itemContent = (
    <ItemContent
      key={item.guid}
      item={item}
      product={product}
      menuGuid={props.menuGuid}
      menuConfig={props.menuConfig}
      templateOverrides={templateOverrides}
      isReadOnly={isReadOnly}
    />
  )

  return (
    <>
      {isReadOnly &&
      (menuTemplateConfig === MenuTemplate.Condensed ||
        menuTemplateConfig === MenuTemplate.ExtraCondensed ||
        menuTemplateConfig === MenuTemplate.Stacked) ? (
        <li
          key={item.guid || item.name}
          ref={ref}
          onFocus={handleFocus}
          style={{
            ...itemStyle,
            pointerEvents: disableClickEvents ? 'none' : 'auto'
          }}
          className={classnames(
            'item',
            templateOverrides?.cardOrientation,
            getTemplateClass(menuTemplateConfig)
          )}
          data-testid='menu-item-card'
        >
          {itemContent}
        </li>
      ) : (
        <li
          key={item.guid || item.name}
          ref={ref}
          onFocus={handleFocus}
          className={classnames(
            'item',
            templateOverrides?.cardOrientation,
            templateOverrides?.backgroundShade,
            templateOverrides?.borderStroke,
            templateOverrides?.roundedCorner,
            templateOverrides?.dropShadow,
            getTemplateClass(menuTemplateConfig),
            {
              clickable: true,
              outOfStock: shouldApplyOutOfStockStyles(item, channel),
              mobileOptimized: templateOverrides?.enableMobileOptimization,
              variableHeight: variableMenuSpacing
            }
          )}
          style={{
            ...itemStyle,
            borderColor,
            pointerEvents: disableClickEvents ? 'none' : 'auto'
          }}
          data-testid='menu-item-card'
        >
          {showItemPDP ? (
            <Link
              href={productDisplayPageHref}
              state={product ? { product } : { menuItem: item }}
              style={{
                ...itemStyle,
                pointerEvents: disableClickEvents ? 'none' : 'auto'
              }}
              testId='link-to-pdp'
            >
              {itemContent}
            </Link>
          ) : (
            <>
              <a
                data-testid={'add-to-cart-' + item.guid}
                href={itemReference}
                id={props.id}
                onClick={onItemClick}
                onKeyDown={onKeyDown}
                tabIndex={0}
                aria-label={item.name ?? undefined}
                aria-haspopup='dialog'
                style={{
                  ...itemStyle,
                  pointerEvents: disableClickEvents ? 'none' : 'auto'
                }}
              >
                {itemContent}
              </a>
              <ItemModal
                withTransitions
                isOpen={isOpen}
                onClose={closeModal}
                itemGroupGuid={item.itemGroupGuid}
                itemMenuGuid={props.menuGuid}
                itemGuid={item.guid}
                itemTags={item.itemTags}
                channelGuid={props.channelGuid}
                itemSource={itemSource ?? ItemSource.Menu}
                onSuccessfulAddToCart={onItemSuccessfullyAdded}
              />
            </>
          )}
          {!isReadOnly &&
            !orderingDisabled &&
            !item.outOfStock &&
            !product &&
            channel === Channel.OO && (
              <QuickAddToCart
                showSuccessIcon={showQuickAddSuccessIcon}
                onItemSuccessfullyAdded={onItemSuccessfullyAdded}
                channelGuid={props.channelGuid}
                itemSource={itemSource ? itemSource : ItemSource.Menu}
                item={item}
                openItemModal={onItemClick}
                variableMenuSpacing={variableMenuSpacing}
              />
            )}
        </li>
      )}
    </>
  )
}

export const onItemClickImpl = (
  e: React.KeyboardEvent | React.MouseEvent | undefined,
  isReadOnly: boolean | undefined,
  item: MenuItem,
  track: ((name: any, data: any) => void) | undefined,
  orderPath: string,
  openModal: () => void,
  cartGuid?: string | null,
  deliveryInfo?: CartDeliveryInfo | null,
  itemSource?: ItemSource
) => {
  e?.preventDefault()
  e?.stopPropagation()
  track?.(TrackingEvents.CLICKED_MENU_ITEM, {
    cartGuid: cartGuid,
    guid: item.guid,
    name: item.name || CLICKED_MENU_ITEM_EVENT_NO_NAME_STR,
    readOnly: !!isReadOnly,
    outOfStock: !!item.outOfStock,
    hasImage: Boolean(item.image),
    descLength: item.description ? item.description.length : 0,
    timeBasedRule: item.timeBasedRules?.leadTimeRule
      ? 'Minimum lead time'
      : item.timeBasedRules?.preorderRule
      ? 'Preorder'
      : null,
    validDeliveryAddressPresent: !!deliveryInfo,
    itemSource
  })
  if (!item.outOfStock) {
    openModal()
    if (!isReadOnly) {
      replaceUrl(menuItemPath(orderPath, item.name || '', item.guid))
    }
  }
}

export const onItemKeydownImpl = (
  e: React.KeyboardEvent,
  isReadOnly: boolean | undefined,
  item: MenuItem,
  track: ((name: any, data: any) => void) | undefined,
  orderPath: string,
  openModal: () => void,
  cartGuid?: string | null,
  deliveryInfo?: CartDeliveryInfo | null,
  itemSource?: ItemSource
) => {
  if (e.type === 'keydown' && e.code !== 'Space' && e.code !== 'Enter') {
    return // only continue if the spacebar or enter key is pressed
  }
  onItemClickImpl(
    e,
    isReadOnly,
    item,
    track,
    orderPath,
    openModal,
    cartGuid,
    deliveryInfo,
    itemSource
  )
}

export const menuItemPath = (
  orderPath: string,
  itemName: string,
  itemGuid: string | null
) => `${orderPath}/item-${slugify(itemName)}_${itemGuid}`
export const menuItemPagePath = (
  menuPath: string,
  itemGroupGuid: string,
  itemName: string,
  itemGuid: string | null
) => `${menuPath}/group_${itemGroupGuid}/item-${slugify(itemName)}_${itemGuid}`
export const ecommMenuItemPath = (
  orderPath: string,
  item: MenuItem,
  product?: DoProduct
) => {
  const { name, guid, slug, itemGroupGuid } = item

  // OPTION type products (multiple variants) use /products/v/{product.slug}
  if (product?.slug && product.type === CatalogProductType.Option) {
    return urljoin(orderPath, 'products', 'v', product.slug)
  }

  // For SINGLE type products and regular menu items, use the /products/{item.slug} route
  if (slug) {
    return urljoin(orderPath, 'products', slug)
  }

  // Fallback to old format
  return urljoin(
    orderPath,
    `group_${itemGroupGuid}/item-${slugify(name || '')}_${guid}`
  )
}

export default MenuItemCard
